import { useEffect, useState } from "react";

// Global animated courier loader. lib/api.js dispatches "shipany:api-pending" events for every
// API call; this overlay appears only when a request actually takes time (>250ms, so instant
// actions never flash) and lingers briefly so it never flickers between chained calls.
const FUN_LINES = ["Checking DTDC lanes…", "Comparing prices…", "Talking to couriers…", "Hunting the fastest truck…", "Almost there…"];

export default function CourierLoader() {
  const [visible, setVisible] = useState(false);
  const [label, setLabel] = useState(null);
  const [courier, setCourier] = useState(null);
  const [funIdx, setFunIdx] = useState(-1);

  useEffect(() => {
    let showT = null;
    let hideT = null;
    const onPending = (e) => {
      const n = e.detail?.pending ?? 0;
      if (n > 0) {
        setLabel(e.detail?.label || null);
        setCourier(e.detail?.courier || null);
        clearTimeout(hideT);
        hideT = null;
        if (!showT) showT = setTimeout(() => { setVisible(true); showT = null; }, 250);
      } else {
        clearTimeout(showT);
        showT = null;
        clearTimeout(hideT);
        hideT = setTimeout(() => { setVisible(false); setLabel(null); setCourier(null); }, 350);
      }
    };
    window.addEventListener("shipany:api-pending", onPending);
    return () => {
      window.removeEventListener("shipany:api-pending", onPending);
      clearTimeout(showT);
      clearTimeout(hideT);
    };
  }, []);

  // Fun courier lines cycle only when an operation runs long (>2s visible) — short
  // actions keep the plain "ShipAny is on the move" subtext.
  useEffect(() => {
    if (!visible) { setFunIdx(-1); return; }
    let iv = null;
    const t = setTimeout(() => {
      setFunIdx(0);
      iv = setInterval(() => setFunIdx((i) => (i + 1) % FUN_LINES.length), 2600);
    }, 2000);
    return () => { clearTimeout(t); if (iv) clearInterval(iv); };
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-shipany-navy/30 backdrop-blur-[2px]"
      data-testid="global-courier-loader" role="status" aria-live="polite" aria-label="Processing">
      <style>{`
        @keyframes courier-drive { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
        @keyframes courier-wheel { to { transform: rotate(360deg); } }
        @keyframes courier-road { to { background-position-x: -48px; } }
        @keyframes courier-pkg { 0%,100% { transform: translateY(0) rotate(-8deg); } 50% { transform: translateY(-6px) rotate(8deg); } }
      `}</style>
      <div className="flex flex-col items-center rounded-3xl border border-slate-100 bg-white px-10 pb-6 pt-8 shadow-2xl">
        <div className="relative flex items-end px-1" style={{ animation: "courier-drive 0.6s ease-in-out infinite" }}>
          {/* trailer */}
          <div className="relative flex h-12 w-20 items-center justify-center rounded-md bg-shipany-blue shadow-md">
            <span className="max-w-[72px] truncate px-1 font-display text-[10px] font-black italic tracking-tight text-white" data-testid="loader-trailer-brand">{courier || "ShipAny"}</span>
            {/* floating package */}
            <div className="absolute -top-3 right-1.5 h-4 w-4 rounded-[3px] bg-amber-400 ring-1 ring-amber-500" style={{ animation: "courier-pkg 0.8s ease-in-out infinite" }} />
          </div>
          {/* cabin */}
          <div className="relative ml-1 h-8 w-7 rounded-tr-md bg-shipany-cyan shadow-md">
            <div className="absolute left-1 top-1 h-3 w-3.5 rounded-sm bg-white/80" />
          </div>
          {/* wheels */}
          <div className="absolute -bottom-2 left-2.5 h-4 w-4 rounded-full border-[3px] border-slate-700 bg-white" style={{ animation: "courier-wheel 0.5s linear infinite" }}>
            <span className="absolute left-1/2 top-0 h-full w-0.5 -translate-x-1/2 bg-slate-400" />
          </div>
          <div className="absolute -bottom-2 right-1.5 h-4 w-4 rounded-full border-[3px] border-slate-700 bg-white" style={{ animation: "courier-wheel 0.5s linear infinite" }}>
            <span className="absolute left-1/2 top-0 h-full w-0.5 -translate-x-1/2 bg-slate-400" />
          </div>
        </div>
        {/* road with moving dashes */}
        <div className="mt-3 h-1 w-44 rounded-full"
          style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(11,92,215,0.55) 0 24px, transparent 24px 48px)", animation: "courier-road 0.45s linear infinite" }} />
        <p className="mt-4 text-xs font-bold uppercase tracking-[0.22em] text-shipany-navy" data-testid="loader-label">{label || "Processing…"}</p>
        <p className="mt-0.5 text-[11px] text-slate-400 transition-opacity" data-testid="loader-fun-line">{funIdx >= 0 ? FUN_LINES[funIdx] : "ShipAny is on the move"}</p>
      </div>
    </div>
  );
}
